// 9fbef606107a605d69c0edbcd8029e5d
import { loadLayoutData, loadModulesData } from '@tcp/core/src/reduxStore/actions';
import styliticsProductListing from '@tcp/core/src/services/abstractors/styliticsProductListing';
import { call, put, takeLatest, select, fork, join, delay } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import { getUnbxdXappConfigs } from '@tcp/core/src/components/features/browse/ProductListing/container/ProductListing.saga';
import { getStringAfterSplit, getMultipackCount, filterMultiPack } from '@tcp/core/src/utils/utils';
import MAX_PRODUCT_PART_NUMBER_ALLOWED from '@tcp/core/src/constants/numeric.constants';
import {
  updateColorFitsSizesMapWithSFCCInventoryData,
  updateRawDataWithSFCCInventoryData,
  isPureVMPProduct,
} from '@tcp/core/src/services/abstractors/productListing/ProductDetail.util';
import { getNavTree } from '@tcp/core/src/components/features/browse/BundleProduct/container/BundleProduct.selectors.js';
import OUTFIT_DETAILS_CONSTANTS from './OutfitDetails.constants';
import {
  setCurrentOutfitAction,
  setLoadingState,
  setOutfitCacheData,
  updateSelectedOutfitProductDetails,
} from './OutfitDetails.actions';
import { getDefaultWishList } from '../../Favorites/container/Favorites.saga';
import getOutfitProdutsDetails from '../../../../../services/abstractors/productListing/outfitDetails';
import {
  getIsPdpServiceEnabled,
  getABTestPdpService,
  getIsPDPInventoryCheckEnabled,
  getOutfitDetailsPageRedesign,
  getIsExtendedInventoryPromoMessagingEnabled,
} from '../../../../../reduxStore/selectors/session.selectors';
import { getOutfitCache, getSelectedProductId, getOutfitId } from './OutfitDetails.selectors';

import logger from '../../../../../utils/loggerInstance';
import getProductInfoById, {
  layoutResolver,
  sfccInventoryApi,
} from '../../../../../services/abstractors/productListing/productDetail';

const checkInCache = (outfitCache, outfitId) => {
  const keys = Object.keys(outfitCache);
  let outfitCachedData;
  keys.forEach((key) => {
    if (key === outfitId.toString()) {
      outfitCachedData = outfitCache[key];
    }
  });
  const { products, outfitData } = outfitCachedData || {};
  return { outfitData, products };
};

function* loadOutfitProducts(products, apiConfig, pageName) {
  if (products && products.length > 0) {
    const category = products[0] && products[0].category;
    const { layout, modules } = yield call(layoutResolver, { category, pageName, apiConfig });
    yield put(loadLayoutData(layout, pageName));
    yield put(loadModulesData(modules));
  }
}

const getProductsLength = (products = []) => {
  return products?.length || 0;
};

const isVMPEnabledProduct = ({ product, getMultiPackAllColor }) => {
  if (!product) {
    return false;
  }

  return getMultiPackAllColor?.find((item) => item?.prodpartno === product.productId)?.isVMPEnabled;
};

const isPureVMP = (styleType, isVMPEnabled) => {
  return isPureVMPProduct(styleType) && isVMPEnabled === 'true';
};

function* callSFCCInventoryAPI(res) {
  let taskData = [];
  const products = res?.products;
  const productsLength = getProductsLength(products);

  try {
    let INITIAL_VALUE = 0;
    let MAX_VALUE = MAX_PRODUCT_PART_NUMBER_ALLOWED;
    let finalProductsList = [];
    for (let i = 0; i < productsLength; i += 1) {
      const isVMPEnabled = isVMPEnabledProduct({
        product: products[i],
        getMultiPackAllColor: products[i]?.getMultiPackAllColor,
      });
      const allProducts = isPureVMP(products[i]?.TCPStyleType, isVMPEnabled)
        ? []
        : products[i]?.allProductPartNos;
      if (products[i]?.getNewMultiPackAllColor?.length && isVMPEnabled === 'true') {
        products[i].getNewMultiPackAllColor.forEach((item) => {
          if (item?.prodpartno) {
            allProducts.push(item?.prodpartno);
          }
        });
      }
      finalProductsList = [...finalProductsList, ...new Set(allProducts)];
    }
    let finalProductsListLength = finalProductsList?.length || 0;
    // To make multiple calls when allProductPartNosLength is greater that maximum allowed productPartNo value
    while (finalProductsListLength > 0) {
      const sfccTaskData = yield fork(
        sfccInventoryApi,
        finalProductsList?.slice(INITIAL_VALUE, MAX_VALUE)
      );
      if (sfccTaskData) {
        taskData = [...taskData, sfccTaskData];
      }
      // Set the INITIAL_VALUE to max value, add maximum allowed prodPartNo to MAX_VALUE, and reduce the allProductParNos length by maximum allowed prodPartNo value for next iteration
      INITIAL_VALUE = MAX_VALUE;
      MAX_VALUE += MAX_PRODUCT_PART_NUMBER_ALLOWED;
      finalProductsListLength -= MAX_PRODUCT_PART_NUMBER_ALLOWED;
    }
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while calling SFCC API in OutfitDetail',
        componentName: 'OutfitDetail.saga',
        methodName: 'callSFCCInventoryAPI',
        res,
      },
    });
  }
  return taskData;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function* updateUnbxdDataWithSFCCInventoryData({ res, taskDataObject }) {
  try {
    const taskData = yield join(taskDataObject);
    const isExtendedInventoryPromoMessagingEnabled = yield select(
      getIsExtendedInventoryPromoMessagingEnabled
    );
    const products = res?.products;
    const productsLength = getProductsLength(products);
    const clonedProducts = products && JSON.parse(JSON.stringify(products));
    let data = [];
    for (let i = 0; i < taskData.length; i += 1) {
      const apiResponse = yield join(taskData[i]);
      if (apiResponse) {
        data = [...data, ...apiResponse];
      }
    }

    for (let i = 0; i < productsLength; i += 1) {
      const isGiftCardProduct = products[i]?.isGiftCard;
      const isAccessoryProduct = !isEmpty(products[i]?.accessoriesData?.accessoriesProductID);
      const shouldUpdateInventoryAttributesCheck =
        isExtendedInventoryPromoMessagingEnabled && !isGiftCardProduct && !isAccessoryProduct;

      const updatedColorFitsSizesMap = updateColorFitsSizesMapWithSFCCInventoryData({
        colorFitsSizesMap: products[i]?.colorFitsSizesMap,
        data,
        shouldUpdateInventoryAttributesCheck,
      });

      const updatedAllProductId = updateRawDataWithSFCCInventoryData(
        products[i].allProductId,
        data
      );
      const updatedMultiPackAllColor = updateRawDataWithSFCCInventoryData(
        products[i].getMultiPackAllColor,
        data
      );

      const updatedCategoryIdList = updateRawDataWithSFCCInventoryData(
        products[i].categoryIdList,
        data,
        false,
        true
      );

      const updatedNewCategoryIdList = updateRawDataWithSFCCInventoryData(
        products[i].newCategoryIdList,
        data,
        true,
        false
      );

      if (products[i]?.getNewMultiPackAllColor?.length) {
        const updatedNewMultiPackAllColor = updateRawDataWithSFCCInventoryData(
          products[i]?.getNewMultiPackAllColor,
          data,
          false,
          false
        );
        const updatedNewPartNumber = updateRawDataWithSFCCInventoryData(
          products[i]?.newPartNumber,
          data,
          false,
          true
        );
        clonedProducts[i].getNewMultiPackAllColor = updatedNewMultiPackAllColor;
        clonedProducts[i].newPartNumber = updatedNewPartNumber;
      }

      if (shouldUpdateInventoryAttributesCheck) {
        const currentProductInventoryData = data.find((item) => item.id === products[i]?.productId);

        if (currentProductInventoryData?.price_max) {
          clonedProducts[i].highOfferPrice = currentProductInventoryData?.price_max;
        }
        if (currentProductInventoryData?.price) {
          clonedProducts[i].offerPrice = currentProductInventoryData?.price;
        }
      }

      clonedProducts[i].colorFitsSizesMap = updatedColorFitsSizesMap;
      clonedProducts[i].allProductId = updatedAllProductId;
      clonedProducts[i].getMultiPackAllColor = updatedMultiPackAllColor;
      clonedProducts[i].categoryIdList = updatedCategoryIdList;
      clonedProducts[i].newCategoryIdList = updatedNewCategoryIdList;
    }
    res.products = clonedProducts;
    yield put(setCurrentOutfitAction({ ...res }));
    yield put(setOutfitCacheData({ outfitData: { ...res }, products: clonedProducts }));
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while updating unbxd data with SFCC data in OutfitDetail',
        componentName: 'OutfitDetail.saga',
        methodName: 'updateUnbxdDataWithSFCCInventoryData',
        res,
        taskDataObject,
      },
    });
  }
}

/**
 * Moves the element at the given index to the start of the array.
 * @param {Array} arr - The array to modify.
 * @param {number} index - The index of the element to move to the start.
 * @returns {Array} A new array with the element at the start and the original array unchanged.
 */
function moveElementToStart(arr, index) {
  if (index < 0 || index >= arr.length) {
    return arr; // Return the original array if indices are out of bounds
  }
  const element = arr[index]; // Get the element at the given index
  return [element, ...arr.slice(0, index), ...arr.slice(index + 1)]; // Create a new array with the element at the start
}

// eslint-disable-next-line
function* loadOutfitDetails({
  payload: {
    outfitId,
    vendorColorProductIdsList,
    fromOutFitPage,
    apiConfig,
    fromOutfitCarousel,
    debounce,
  },
}) {
  try {
    // for mobile only, on outfit page, new design and is from outfit
    const outfitCache = yield select(getOutfitCache);
    const pdpProductId = yield select(getSelectedProductId);
    const isOutfitDetailsPageRedesign = yield select(getOutfitDetailsPageRedesign);
    const pageName = 'pdp';
    const { outfitData, products: cacheProducts } = checkInCache(outfitCache, outfitId);
    if (!outfitData || !fromOutfitCarousel) {
      const state = yield select();
      const xappURLConfig = yield call(getUnbxdXappConfigs);
      let resurl;
      const navigationTree = state.Navigation.navigationData;
      if (debounce) {
        yield delay(debounce);
      }
      if (!outfitData) yield put(setLoadingState({ isLoading: true }));
      if (fromOutFitPage) {
        resurl = yield call(styliticsProductListing.getOutfitData, outfitId);
      }
      const getUrl = fromOutFitPage ? resurl?.largeImageUrl : '';
      const isPdpProductServiceEnabled = yield select(getIsPdpServiceEnabled);
      const enablePDPServiceABTest = yield select(getABTestPdpService);
      const res = yield call(
        getOutfitProdutsDetails,
        {
          outfitId,
          vendorColorProductIdsList,
          getImgPath: () => {},
          navigationTree,
          state,
          largeImgUrl: getUrl,
        },
        xappURLConfig,
        isPdpProductServiceEnabled,
        enablePDPServiceABTest
      );

      const { products } = res;
      if (pdpProductId && isOutfitDetailsPageRedesign) {
        const index = products?.findIndex((product) => product?.generalProductId === pdpProductId);
        if (index > 0) {
          res.products = moveElementToStart(products, index);
        }
      }
      let outfitProductItemPartId;
      let partId;
      const productsLength = products ? products.length : 0;
      for (let i = 0; i < productsLength; i += 1) {
        const outfitProductItem = products[i];
        if (!!outfitProductItem && outfitProductItem.multiPackUSStore) {
          let multiplePartId = '';
          partId = outfitProductItem.tcpMultiPackReferenceUSStore.join(',').split(',');
          const updatedPartId = new Set(partId);
          const findUpdatedPartId = [...updatedPartId];
          const findUpdatedPartIdLength = findUpdatedPartId ? findUpdatedPartId.length : 0;

          for (let id = 0; id < findUpdatedPartIdLength; id += 1) {
            if (findUpdatedPartId[id].includes(',')) {
              const arrayOfPartNum = getStringAfterSplit(findUpdatedPartId[id], ',');
              const arrayOfPartNumLength = arrayOfPartNum ? arrayOfPartNum.length : 0;

              for (let num = 0; num < arrayOfPartNumLength; num += 1) {
                multiplePartId = multiplePartId.concat(
                  getStringAfterSplit(arrayOfPartNum[num], '#')[0]
                );
                if (num !== arrayOfPartNumLength - 1) {
                  multiplePartId = multiplePartId.concat(',');
                }
              }
            } else {
              outfitProductItemPartId = [getStringAfterSplit(findUpdatedPartId[id], '#')[0]];
              multiplePartId += `${outfitProductItemPartId},`;
            }
          }

          if (findUpdatedPartIdLength) {
            const newProductDetail = yield call(
              getProductInfoById,
              xappURLConfig,
              multiplePartId,
              state,
              undefined,
              undefined,
              false,
              '',
              true,
              partId,
              undefined,
              undefined,
              isPdpProductServiceEnabled,
              enablePDPServiceABTest
            );

            if (!!outfitProductItem && outfitProductItem.multiPackUSStore) {
              const newMultiPackCount =
                outfitProductItem &&
                outfitProductItem.multiPackUSStore &&
                outfitProductItem.tcpMultiPackReferenceUSStore &&
                outfitProductItem.tcpMultiPackReferenceUSStore[0].split(',');
              const multiPackCount = newMultiPackCount ? getMultipackCount(newMultiPackCount) : 0;

              if (newProductDetail) {
                const getFilterData = newProductDetail.product.categoryIdList;
                const getmultipackProduct = getFilterData && filterMultiPack(getFilterData);
                products[i].newPartNumber = getmultipackProduct;
                products[i].multiPackCount = multiPackCount;
                products[i].getMultiPackAllColor = outfitProductItem.getMultiPackAllColor;
                products[i].getNewMultiPackAllColor = newProductDetail.product.getMultiPackAllColor;
                products[i].partIdDetail = newProductDetail.product.partIdDetails;
              } else {
                products[i].newPartNumber = null;
                products[i].multiPackCount = null;
                products[i].getMultiPackAllColor = null;
              }
            }
          }
        }
      }

      const isPDPInventoryCheckEnabled = yield select(getIsPDPInventoryCheckEnabled);
      let taskDataObject;
      if (isPDPInventoryCheckEnabled) {
        taskDataObject = yield fork(callSFCCInventoryAPI, res);
      }
      yield put(setCurrentOutfitAction({ ...res }));
      yield call(getDefaultWishList);
      yield put(setOutfitCacheData({ outfitData: { ...res }, products }));
      yield put(setLoadingState({ isLoading: false }));
      loadOutfitProducts(products, apiConfig, pageName);
      if (isPDPInventoryCheckEnabled && res && taskDataObject) {
        yield updateUnbxdDataWithSFCCInventoryData({
          res,
          taskDataObject,
        });
      }
    } else {
      yield put(setLoadingState({ isLoading: false }));
      yield put(setCurrentOutfitAction({ ...outfitData }));
      loadOutfitProducts(cacheProducts, apiConfig, pageName);
    }
  } catch (err) {
    logger.error('error: ', err);
    yield put(setLoadingState({ isLoading: false }));
  }
}

function* fetchSelectedOutfitFitProductDetails(args) {
  try {
    const { payload } = args;
    const { isOutfitPage, vendorColorProductIdsList, outfitItemIndex } = payload;
    const state = yield select();

    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const navigationTree = yield select(getNavTree);
    const isPdpProductServiceEnabled = yield select(getIsPdpServiceEnabled);
    const enablePDPServiceABTest = yield select(getABTestPdpService);
    const outfitId = yield select(getOutfitId);
    let resurl;
    if (isOutfitPage) {
      resurl = yield call(styliticsProductListing.getOutfitData, outfitId);
    }
    const getUrl = isOutfitPage ? resurl?.largeImageUrl : '';

    const res = yield call(
      getOutfitProdutsDetails,
      {
        outfitId,
        vendorColorProductIdsList,
        getImgPath: () => {},
        navigationTree,
        state,
        largeImgUrl: getUrl,
      },
      xappURLConfig,
      isPdpProductServiceEnabled,
      enablePDPServiceABTest
    );
    yield put(
      updateSelectedOutfitProductDetails({
        product: res?.products[0],
        outfitItemIndex,
      })
    );
  } catch (error) {
    logger.error({
      error: `Error in OutfitDetailsSaga - fetchSelectedOutfitFitProductDetails catch - ${error}`,
      extraData: {
        component: 'OutfitDetailsSaga - fetchSelectedOutfitFitProductDetails',
      },
    });
  }
}

function* OutfitDetailsSaga() {
  yield takeLatest(OUTFIT_DETAILS_CONSTANTS.FETCH_OUTFIT_PRODUCTS, loadOutfitDetails);
  yield takeLatest(
    OUTFIT_DETAILS_CONSTANTS.FETCH_SELECTED_OUTFIT_FIT_PRODUCT,
    fetchSelectedOutfitFitProductDetails
  );
}

export default OutfitDetailsSaga;
